import { useEffect, useState } from 'react';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const query = (): MediaQueryList | undefined =>
  typeof window === 'undefined' ? undefined : window.matchMedia?.(REDUCED_MOTION_QUERY);

/**
 * Whether the reader has asked the OS for less motion — `undefined` only while rendering on the
 * server, where there is nothing to ask. The native sibling reads asynchronously, hence the shape.
 */
const useReducedMotion = (): boolean | undefined => {
  const [isReducedMotion, setIsReducedMotion] = useState<boolean | undefined>(
    () => query()?.matches
  );

  useEffect((): (() => void) | undefined => {
    const list = query();
    if (!list) return undefined;

    setIsReducedMotion(list.matches);

    const handleChange = (event: MediaQueryListEvent): void => setIsReducedMotion(event.matches);
    // Safari below 14 only has the deprecated pair, as does the jsdom stand-in under test.
    if (list.addEventListener) {
      list.addEventListener('change', handleChange);
      return () => list.removeEventListener('change', handleChange);
    }

    list.addListener(handleChange);
    return () => list.removeListener(handleChange);
  }, []);

  return isReducedMotion;
};

export default useReducedMotion;
